import { faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { faFileArrowDown, faThumbsUp as faThumbsUpSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { hasPermission } from 'Components/Authorize';
import LikeButton from 'Components/LikeButton';
import { useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { API_BASE_URL } from 'Shared/fetch';
import { logGAEvent } from 'Shared/ga';
import { trackMP } from 'Shared/mp';
import useFetch from 'use-http';
import { Permission } from 'zl-shared';

import { updateMediaInQueryClient } from '@/queries/media';
import { useSelector } from '@/redux/helper';
import { selectCanDownloadMediaWithoutWatermark } from '@/redux/selectors/users';
import { updateAlbumData } from '@/redux/slices/myAlbum/actions';

import { androidDevice, getDeviceType, getShareableMediaUrl, iOSDevice } from '../../../helpers';
import store from '../../../redux/store';
import { updateHabitatAlbumData } from '../../../routes/habitat/components/Album/actions';
import { CopyLink, DeleteMediaButton, DownloadButton, FacebookShareLink, WebShare } from './helper';

const ShareButtons = ({ mediaId, data, slug, slugPrefix, hashtags, userId, onDeleteSuccess }) => {
  const queryClient = useQueryClient();
  const { url, videoURL, habitatId, isLiked, usersLike, htmlURL, userId: ownerId, highlight } = data ?? {};
  const mediaType = data?.mediaType ?? data?.type;
  const videoType = data?.videoType ?? data?.type;
  const type = data?.type ?? videoType ?? mediaType;

  const dispatch = useDispatch();

  const role = useSelector((state) => state.user.role);
  const canDownloadMediaWithoutWatermark = useSelector(selectCanDownloadMediaWithoutWatermark);
  const [isHighlighted, setIsHighlighted] = useState(highlight);
  const { post: sharePost } = useFetch(API_BASE_URL, {
    credentials: 'include',
    cachePolicy: 'no-cache',
  });
  const { put } = useFetch(API_BASE_URL, {
    credentials: 'include',
    cachePolicy: 'no-cache',
  });

  const logShare = async (platform) => {
    let habitatInfo = store.getState()?.habitat?.habitatInfo;
    if (videoType === 'trailer') {
      habitatInfo = store.getState()?.allHabitats?.filter((i) => i?._id === data?.habitat?.id)[0];
    }
    const shareLogObj = {
      userId: userId ?? 'guest',
      mediaId,
      mediaType: type,
      platform,
      slug,
      slugPrefix,
      deviceType: getDeviceType(),
      applicationPath: document.location.pathname.startsWith('/h') ? 'habitat' : 'publicAlbum',
      habitatId: habitatInfo?._id ?? habitatId,
    };
    trackMP('shared-ugc', shareLogObj);
    await sharePost('/logs/share', shareLogObj);
  };

  const onHighlightClick = async () => {
    const newHighLight = !isHighlighted;
    setIsHighlighted(newHighLight);
    const payload = { id: mediaId, data: { ...data, highlight: newHighLight } };
    dispatch(updateHabitatAlbumData(payload));
    dispatch(updateAlbumData(payload));

    try {
      await put(`/videos/${mediaId}`, { highlight: newHighLight });
      updateMediaInQueryClient(queryClient, mediaId, { highlight: newHighLight });
    } catch (e) {
      console.error('api error', e);
    }
  };

  return (
    <div className="flex w-full justify-between">
      <div className="mx-2 flex items-center gap-2">
        {/* Copy Link Button */}
        {(slugPrefix || htmlURL) && (
          <CopyLink
            className="size-8 text-lg text-grey-1 hover:text-grey-2"
            link={getShareableMediaUrl(slugPrefix, mediaId) || htmlURL}
            onClick={() => logShare('shareLinkCopy')}
          />
        )}

        {/* Web Share API Button */}
        {(androidDevice() || iOSDevice()) && htmlURL && (
          <WebShare
            className="size-8 text-lg text-grey-1 hover:text-grey-2"
            logShare={logShare}
            width={20}
            radius={20}
            htmlURL={htmlURL}
          />
        )}

        {/* FB Share Button */}
        {htmlURL && (
          <FacebookShareLink
            className="size-8 text-lg text-grey-1 hover:text-grey-2"
            htmlURL={htmlURL}
            hashtags={hashtags}
            onClick={() => logShare('facebook')}
          />
        )}

        {/* Like Button */}
        <LikeButton
          noCount
          data={{ isLiked, likes: usersLike }}
          className={clsx('size-8 text-lg', isLiked ? 'text-system-pink' : 'text-grey-1 hover:text-grey-2')}
          onClick={() => logGAEvent('ugc', `react-liked-${type}`, slug, slugPrefix)}
          mediaId={mediaId}
          type={type}
        />

        {/* Download Button */}
        {(videoType === 'clip' || mediaType === 'photo') && (
          <DownloadButton className="size-8 text-lg text-grey-1 hover:text-grey-2" url={url || videoURL} data={data} />
        )}
      </div>
      <div className="mx-2 flex items-center justify-center gap-2">
        {canDownloadMediaWithoutWatermark && (data?.rawVideoURL || data?.rawURL) && (
          <DownloadButton
            className="size-8 text-lg text-system-pink"
            url={data?.rawVideoURL || data?.rawURL}
            data={data}
            icon={faFileArrowDown}
          />
        )}
        {['admin', 'moderator'].includes(role) && mediaType !== 'photo' && (
          <button
            type="button"
            className="preflight preflight-button flex size-8 items-center justify-center text-lg text-system-pink"
            onClick={onHighlightClick}
          >
            <FontAwesomeIcon icon={isHighlighted ? faThumbsUpSolid : faThumbsUp} />
          </button>
        )}
        {/* Delete button */}
        {(ownerId === userId || hasPermission(Permission.Habitat.EditAlbumMediaHide)) && (
          <DeleteMediaButton
            className="size-8 text-lg text-system-pink"
            mediaId={mediaId}
            type={mediaType}
            onDeleteSuccess={onDeleteSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default ShareButtons;
